// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/fenced-imports */
import { CustomEditorTemplateProps, ImageMapPickerDefinition } from "@bg/CustomFieldDefinition";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Button from "@insite/mobius/Button";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TextField from "@insite/mobius/TextField";
import Typography from "@insite/mobius/Typography";
import ShellState from "@insite/shell/Store/ShellState";
import Tippy, { TippyProps } from "@tippyjs/react";
import * as React from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";
import { inlinePositioning } from "tippy.js";

type OwnProps = CustomEditorTemplateProps<ImageMapPickerValue[], ImageMapPickerDefinition>;

const mapStateToProps = (state: ShellState, ownProps: OwnProps) => {
    const coordinates = state.data.pages.widgetsById[(ownProps.item as any).id]
        ? state.data.pages.widgetsById[(ownProps.item as any).id].fields.coords
        : [];
    return {
        coordinates,
    };
};

interface ImageMapPickerValue {
    xPos: number;
    yPos: number;
    productId: string;
    pointColor: string;
}

type State = {
    imageLoaded: boolean;
    coords: ImageMapPickerValue[];
    selectedCoords: ImageMapPickerValue;
    imageUrl: string;
    imageElement: any;
    tempSku: string;
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

class ImageMapPickerField extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            imageLoaded: false,
            coords: [],
            selectedCoords: { xPos: 0, yPos: 0, productId: "", pointColor: "#ffffff" },
            imageUrl: "",
            imageElement: null,
            tempSku: "",
        };
    }

    componentDidMount() {
        const imageElement = document.getElementById(`image_${this.state.imageUrl}`);
        this.setState({
            imageUrl: this.props.fieldDefinition.imageUrl(this.props.item),
            imageElement,
        });
        this.setState({ coords: this.props.fieldValue });
    }

    componentDidUpdate(_prevProps: Props) {
        if (!this.state.imageElement && this.state.imageLoaded && this.state.imageUrl) {
            this.setImageElement();
        }
        if (
            this.state.imageUrl &&
            !this.state.imageLoaded &&
            !this.state.coords.length &&
            this.props.coordinates.length
        ) {
            this.setCoordsFromShellState();
        }
    }

    setCoordsFromShellState = () => {
        this.setState({ coords: this.props.fieldValue });
    };

    setImageElement = () => {
        const imageElement = document.getElementById(`image_${this.state.imageUrl}`);
        this.setState({
            imageElement,
        });
    };

    closeModalHandler = () => {
        this.setState({ imageLoaded: false });
    };

    getCoordinates = (e: any) => {
        let posX = 0;
        let posY = 0;
        let event = e;
        let coords: ImageMapPickerValue[] = this.state.coords;
        const imgPosY = e.target.getBoundingClientRect().top;
        const imgPosX = e.target.getBoundingClientRect().left;
        if (!event) {
            event = window.event;
        }
        if (e.pageX || e.pageY) {
            posX = event.pageX;
            posY = event.pageY;
        } else if (event.clientX || event.clientY) {
            posX = event.clientX + document.body.scrollLeft + document.documentElement.scrollLeft;
            posY = event.clientY + document.body.scrollTop + document.documentElement.scrollTop;
        }

        posX = posX - imgPosX;
        posY = posY - imgPosY;
        coords = [...coords, { xPos: posX, yPos: posY, productId: "", pointColor: "#ffffff" }];
        this.props.updateField(this.props.fieldDefinition.name, coords);
        this.setState({ coords });
    };

    handleProductSelection = (e: any, index: number) => {
        const coords = [...this.state.coords];
        const coord = {} as ImageMapPickerValue;
        coord.pointColor = coords[index].pointColor;
        coord.productId = e.target.value;
        coord.xPos = coords[index].xPos;
        coord.yPos = coords[index].yPos;
        coords[index] = { ...coord };
        this.setState({ coords });
        this.props.updateField(this.props.fieldDefinition.name, coords);
    };

    handlePointColorSelection = (e: any, index: number) => {
        const coords = [...this.state.coords];
        const coord = {} as ImageMapPickerValue;
        coord.pointColor = e.target.value;
        coord.productId = coords[index].productId;
        coord.xPos = coords[index].xPos;
        coord.yPos = coords[index].yPos;
        coords[index] = { ...coord };
        this.setState({ coords });
        this.props.updateField(this.props.fieldDefinition.name, coords);
    };

    openModal = () => {
        this.setState({ imageLoaded: true });
    };

    savePoints = () => {
        this.setState({ imageLoaded: false });
        const coords = this.state.coords;
        this.props.updateField(this.props.fieldDefinition.name, coords);
    };

    removePoint = (index: number) => {
        const newCoords = [...this.state.coords];
        newCoords.splice(index, 1);
        this.props.updateField(this.props.fieldDefinition.name, newCoords);
        this.setState({ coords: newCoords });
    };

    handleFallbackState = (coords: ImageMapPickerValue[]) => {
        this.setState({ coords });
        this.props.updateField(this.props.fieldDefinition.name, coords);
    };

    render() {
        const { imageLoaded, coords, selectedCoords, imageUrl } = this.state;

        return (
            <StyledWrapper
                css={css`
                    margin: 20px 0;
                `}
            >
                <Typography
                    css={css`
                        margin: 20px 0;
                        font-weight: bold;
                    `}
                    transform="uppercase"
                >
                    {this.props.fieldDefinition.displayName}
                </Typography>
                <Button
                    css={css`
                        background: blue;
                        margin-bottom: 15px;
                    `}
                    onClick={this.openModal}
                >
                    Open Image Modal
                </Button>
                {coords &&
                    coords.length > 0 &&
                    coords.map((coord, index) => (
                        <StyledWrapper
                            key={`${coord.xPos}_${coord.yPos}`}
                            css={css`
                                display: flex;
                                flex-wrap: wrap;
                                justify-content: space-between;
                                margin-bottom: 15px;
                            `}
                        >
                            <Typography
                                size="15px"
                                lineHeight="18px"
                                weight="bold"
                                css={css`
                                    width: 100%;
                                `}
                            >{`POINT ${index + 1}`}</Typography>
                            <TextField
                                disabled={true}
                                cssOverrides={{
                                    formField: css`
                                        max-width: 50%;
                                        padding-right: 20px;
                                    `,
                                }}
                                type="text"
                                value={coord.xPos}
                                label="X"
                            />
                            <TextField
                                disabled={true}
                                cssOverrides={{
                                    formField: css`
                                        max-width: 50%;
                                        padding-left: 20px;
                                    `,
                                }}
                                type="text"
                                value={coord.yPos}
                                label="Y"
                            />
                            <StyledWrapper
                                css={css`
                                    margin-top: 10px;
                                    width: 100%;
                                `}
                            >
                                <TextField
                                    cssOverrides={{
                                        formField: css`
                                            margin-top: 10px;
                                        `,
                                    }}
                                    value={coord.productId}
                                    type="text"
                                    label="Product Erp#"
                                    onChange={e => this.handleProductSelection(e, index)}
                                />
                            </StyledWrapper>
                            <Button
                                css={css`
                                    background: red;
                                    margin-top: 10px;
                                `}
                                onClick={() => this.removePoint(index)}
                            >
                                Remove Point
                            </Button>
                        </StyledWrapper>
                    ))}
                <Modal size={710} isOpen={imageLoaded} handleClose={this.closeModalHandler}>
                    <StyledWrapper
                        css={css`
                            width: 680px;
                            height: 350px;
                            margin-bottom: 10px;
                            position: relative;
                            img {
                                aspect-ratio: 1360 / 700;
                            }
                        `}
                    >
                        <img
                            onMouseDown={this.getCoordinates}
                            id={`image_${imageUrl}`}
                            src={imageUrl}
                            alt="Not Found"
                            width="680px"
                            height="350px"
                        />
                        {coords &&
                            coords.length > 0 &&
                            coords.map((coord, index) => {
                                // eslint-disable-next-line react/display-name
                                const PointItem = React.forwardRef((_props, refs) => (
                                    <StyledWrapper
                                        ref={refs as any}
                                        css={css`
                                            width: 100%;
                                            height: 100%;
                                            display: flex;
                                            justify-content: center;
                                            align-items: center;
                                            cursor: pointer;
                                        `}
                                    >
                                        <Typography size="13px" lineHeight="15px" weight="bold">
                                            {index + 1}
                                        </Typography>
                                    </StyledWrapper>
                                ));

                                return (
                                    <PinPoint
                                        key={`${coord.xPos}-${coord.yPos}`}
                                        pointColor={coord.pointColor}
                                        xPos={coord.xPos}
                                        yPos={coord.yPos}
                                    >
                                        <Tippy
                                            inlinePositioning={true}
                                            interactive={true}
                                            trigger="click"
                                            content={
                                                <ProductSku
                                                    coord={coord}
                                                    index={index}
                                                    coords={coords}
                                                    fallBackState={this.handleFallbackState}
                                                    removeItem={this.removePoint}
                                                />
                                            }
                                            placement="right-start"
                                            popperOptions={{
                                                modifiers: [
                                                    {
                                                        name: "flip",
                                                        options: {
                                                            fallbackPlacements: ["left", "start"],
                                                        },
                                                    },
                                                ],
                                            }}
                                        >
                                            <PointItem />
                                        </Tippy>
                                    </PinPoint>
                                );
                            })}
                    </StyledWrapper>
                    <Button
                        css={css`
                            background: blue;
                        `}
                        onClick={this.savePoints}
                    >
                        Save
                    </Button>
                </Modal>
            </StyledWrapper>
        );
    }
}

const PinPoint = styled.div<any>`
    position: absolute;
    top: ${({ yPos }) => `calc(${yPos}px - 8px)`};
    left: ${({ xPos }) => `calc(${xPos}px - 8px)`};
    background: ${({ pointColor }) => pointColor};
    border-radius: 50%;
    height: 16px;
    width: 16px;
    display: flex;
    align-items: center;
    justify-content: center;

    .tippy-box {
        background: white;
        border-radius: 8px;
        padding: 10px;
        min-width: 200px;
    }
`;

export default connect(mapStateToProps)(ImageMapPickerField);

const ProductSku: React.FC<{
    coord: ImageMapPickerValue;
    coords: ImageMapPickerValue[];
    index: number;
    fallBackState: (coords: ImageMapPickerValue[]) => void;
    removeItem: (index: number) => void;
}> = ({ coord, fallBackState, index, removeItem, coords }) => {
    const [sku, setSku] = React.useState(coord.productId);
    const [pointColor, setPointColor] = React.useState(coord.pointColor);

    const handleSkuChange = (e: any) => {
        setSku(e.target.value);
    };

    const handleColorChange = (e: any) => {
        setPointColor(e.target.value);
    };

    const handleBlur = (e: any) => {
        const newCoords = [...coords];
        let newCoord = {} as ImageMapPickerValue;
        newCoord = { ...coord };
        newCoord.pointColor = pointColor;
        newCoord.productId = sku;
        newCoords[index] = { ...newCoord };
        fallBackState(newCoords);
    };

    return (
        <StyledWrapper>
            <TextField
                cssOverrides={{
                    formField: css`
                        margin-top: 15px;
                    `,
                }}
                value={sku}
                type="text"
                label="Product Erp#"
                onChange={handleSkuChange}
                onBlur={handleBlur}
            />
            <TextField
                type="color"
                cssOverrides={{
                    formField: css`
                        margin-top: 15px;
                    `,
                }}
                value={pointColor}
                onChange={handleColorChange}
                onBlur={handleBlur}
                label="Point Color"
            />
            <Button
                css={css`
                    background: red;
                    margin-top: 15px;
                `}
                onClick={() => removeItem(index)}
            >
                Remove Point
            </Button>
        </StyledWrapper>
    );
};
